import { findInEnum, findValueInEnum, maybe } from "@saleor/misc";
import {
  OrderFilterKeys,
  OrderListFilterOpts
} from "@saleor/orders/components/OrderListPage/filters";

import { IFilterElement } from "../../../components/Filter";
import {
  LanguageCodeFilter,
  OrderFilterInput,
  // OrderReferCode,
  OrderRefercodeFilter,
  OrderStatusFilter,
  PaymentChargeStatusEnum
} from "../../../types/globalTypes";
import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getGteLteVariables,
  getMinMaxQueryParam,
  getMultipleEnumValueQueryParam,
  getSingleValueQueryParam
} from "../../../utils/filters";
import {
  OrderListUrlFilters,
  OrderListUrlFiltersEnum,
  OrderListUrlFiltersWithMultipleValuesEnum,
  OrderListUrlQueryParams
} from "../../urls";

export const ORDER_FILTERS_KEY = "orderFilters";

export function getFilterOpts(
  params: OrderListUrlFilters
): OrderListFilterOpts {
  return {
    created: {
      active: maybe(
        () =>
          [params.createdFrom, params.createdTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.createdTo, ""),
        min: maybe(() => params.createdFrom, "")
      }
    },
    customer: {
      active: !!maybe(() => params.customer),
      value: params.customer
    },
    couponcode: {
      active: !!maybe(() => params.couponcode),
      value: params.couponcode
    },
    languageCode: {
      active: maybe(() => params.languageCode !== undefined, false),
      value: maybe(
        () =>
          dedupeFilter(
            params.languageCode.map(languages =>
              findValueInEnum(languages, LanguageCodeFilter)
            )
          ),
        []
      )
    },
    status: {
      active: maybe(() => params.status !== undefined, false),
      value: maybe(
        () =>
          dedupeFilter(
            params.status.map(status =>
              findValueInEnum(status, OrderStatusFilter)
            )
          ),
        []
      )
    },
    refercode: {
      active: maybe(() => params.refercode !== undefined, false),
      value: maybe(
        () =>
          dedupeFilter(
            params.refercode.map(refercode =>
              findValueInEnum(refercode, OrderRefercodeFilter)
            )
          ),
        []
      )
    },
    paymentStatus: {
      active: maybe(() => params.paymentStatus !== undefined, false),
      value: maybe(
        () =>
          dedupeFilter(
            params.paymentStatus.map(paymentStatus =>
              findValueInEnum(paymentStatus, PaymentChargeStatusEnum)
            )
          ),
        []
      )
    }
  };
}

export function getFilterVariables(
  params: OrderListUrlFilters
): OrderFilterInput {
  return {
    created: getGteLteVariables({
      gte: params.createdFrom,
      lte: params.createdTo
    }),
    customer: params.customer,
    couponcode: params.couponcode,
    search: params.query,
    languageCode: maybe(() =>
      params.languageCode.map(languageCode =>
        findInEnum(languageCode, LanguageCodeFilter)
      )
    ),
    status: maybe(() =>
      params.status.map(status => findInEnum(status, OrderStatusFilter))
    ),
    referCode: maybe(() =>
      params.refercode.map(refercode =>
        findInEnum(refercode, OrderRefercodeFilter)
      )
    ),
    paymentStatus: maybe(() =>
      params.paymentStatus.map(paymentStatus =>
        findInEnum(paymentStatus, PaymentChargeStatusEnum)
      )
    )
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<OrderFilterKeys>
): OrderListUrlFilters {
  const { name } = filter;

  switch (name) {
    case OrderFilterKeys.created:
      return getMinMaxQueryParam(
        filter,
        OrderListUrlFiltersEnum.createdFrom,
        OrderListUrlFiltersEnum.createdTo
      );

    case OrderFilterKeys.status:
      return getMultipleEnumValueQueryParam(
        filter,
        OrderListUrlFiltersWithMultipleValuesEnum.status,
        OrderStatusFilter
      );
    case OrderFilterKeys.refercode:
      return getMultipleEnumValueQueryParam(
        filter,
        OrderListUrlFiltersWithMultipleValuesEnum.refercode,
        OrderRefercodeFilter
      );
    case OrderFilterKeys.paymentStatus:
      return getMultipleEnumValueQueryParam(
        filter,
        OrderListUrlFiltersWithMultipleValuesEnum.paymentStatus,
        PaymentChargeStatusEnum
      );
    case OrderFilterKeys.languageCode:
      return getMultipleEnumValueQueryParam(
        filter,
        OrderListUrlFiltersWithMultipleValuesEnum.languageCode,
        LanguageCodeFilter
      );

    case OrderFilterKeys.customer:
      return getSingleValueQueryParam(filter, OrderListUrlFiltersEnum.customer);

    case OrderFilterKeys.couponcode:
      return getSingleValueQueryParam(
        filter,
        OrderListUrlFiltersEnum.couponcode
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<OrderListUrlFilters>(ORDER_FILTERS_KEY);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  OrderListUrlQueryParams,
  OrderListUrlFilters
>({
  ...OrderListUrlFiltersEnum,
  ...OrderListUrlFiltersWithMultipleValuesEnum
});
